import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
/** @jsx jsx */
import { Fragment } from 'react';
import { css, jsx } from '@emotion/react';
import { ACTION, ACTION_SUBJECT } from '@atlaskit/editor-common/analytics';
import { PortalProviderWithThemeProviders, PortalRenderer } from '@atlaskit/editor-common/portal-provider';
import { BaseTheme, WidthProvider } from '@atlaskit/editor-common/ui';
import { getUiComponent } from '../create-editor';
import ErrorBoundary from '../create-editor/ErrorBoundary';
import { createFeatureFlagsFromProps } from '../create-editor/feature-flags-from-props';
import ReactEditorView from '../create-editor/ReactEditorViewNext';
import { ContextAdapter } from '../nodeviews/context-adapter';
import EditorContext from '../ui/EditorContext';
import { useSetPresetContext } from '../presets/context';
import { RenderTracking } from '../utils/performance/components/RenderTracking';
import useMeasureEditorMountTime from './hooks/useMeasureEditorMountTime';
import useProviderFactory from './hooks/useProviderFactory';
import { getBaseFontSize } from './utils/getBaseFontSize';
/**
 * EditorInternal is used for the internal editor react component
 * with the lifecycle methods extracted into hooks.
 */
export default function EditorInternal(props) {
  var editorProps = props.props,
    getExperienceStore = props.getExperienceStore,
    editorActions = props.editorActions,
    createAnalyticsEvent = props.createAnalyticsEvent;
  useMeasureEditorMountTime(editorProps, getExperienceStore, createAnalyticsEvent);
  var providerFactory = useProviderFactory(editorProps, editorActions, createAnalyticsEvent);
  var updatedProps = _objectSpread(_objectSpread({}, props), {}, {
    providerFactory: providerFactory
  });
  return jsx(EditorInternalWithoutHooks, updatedProps);
}

/**
 * EditorInternalComponent is used to capture the common component
 * from the `render` method of `Editor` and share it with `EditorNext`.
 */
export function EditorInternalWithoutHooks(_ref) {
  var _props$performanceTra, _props$performanceTra2, _props$performanceTra3, _props$performanceTra4;
  var props = _ref.props,
    handleAnalyticsEvent = _ref.handleAnalyticsEvent,
    createAnalyticsEvent = _ref.createAnalyticsEvent,
    handleSave = _ref.handleSave,
    editorActions = _ref.editorActions,
    providerFactory = _ref.providerFactory,
    onEditorCreated = _ref.onEditorCreated,
    onEditorDestroyed = _ref.onEditorDestroyed,
    preset = _ref.preset;
  var Component = getUiComponent(props.appearance);
  var setEditorApi = useSetPresetContext();
  var overriddenEditorProps = _objectSpread(_objectSpread({}, props), {}, {
    onSave: props.onSave ? handleSave : undefined,
    // noop all analytic events, even if a handler is still passed.
    analyticsHandler: undefined
  });
  var featureFlags = createFeatureFlagsFromProps(props);
  var renderTracking = (_props$performanceTra = props.performanceTracking) === null || _props$performanceTra === void 0 ? void 0 : (_props$performanceTra2 = _props$performanceTra.renderTracking) === null || _props$performanceTra2 === void 0 ? void 0 : _props$performanceTra2.editor;
  var renderTrackingEnabled = renderTracking === null || renderTracking === void 0 ? void 0 : renderTracking.enabled;
  var useShallow = renderTracking === null || renderTracking === void 0 ? void 0 : renderTracking.useShallow;
  // ED-16320: Check for explicit disable so that by default
  // it will still be enabled as it currently is. Then we can
  // progressively opt out synthetic tenants.
  var isErrorTrackingExplicitlyDisabled = ((_props$performanceTra3 = props.performanceTracking) === null || _props$performanceTra3 === void 0 ? void 0 : (_props$performanceTra4 = _props$performanceTra3.errorTracking) === null || _props$performanceTra4 === void 0 ? void 0 : _props$performanceTra4.enabled) === false;
  return jsx(Fragment, null, renderTrackingEnabled && jsx(RenderTracking, {
    componentProps: props,
    action: ACTION.RE_RENDERED,
    actionSubject: ACTION_SUBJECT.EDITOR,
    handleAnalyticsEvent: handleAnalyticsEvent,
    propsToIgnore: ['defaultValue'],
    useShallow: useShallow
  }), jsx(ErrorBoundary, {
    errorTracking: !isErrorTrackingExplicitlyDisabled,
    createAnalyticsEvent: createAnalyticsEvent,
    contextIdentifierProvider: props.contextIdentifierProvider,
    featureFlags: featureFlags
  }, jsx(WidthProvider, {
    css: css({
      height: '100%'
    })
  }, jsx(EditorContext, {
    editorActions: editorActions
  }, jsx(ContextAdapter, null, jsx(PortalProviderWithThemeProviders, {
    onAnalyticsEvent: handleAnalyticsEvent,
    useAnalyticsContext: props.UNSAFE_useAnalyticsContext,
    render: function render(portalProviderAPI) {
      return jsx(Fragment, null, jsx(ReactEditorView, {
        editorProps: overriddenEditorProps,
        createAnalyticsEvent: createAnalyticsEvent,
        portalProviderAPI: portalProviderAPI,
        providerFactory: providerFactory,
        onEditorCreated: onEditorCreated,
        onEditorDestroyed: onEditorDestroyed,
        disabled: props.disabled,
        preset: preset,
        setEditorApi: setEditorApi,
        render: function render(_ref2) {
          var _props$featureFlags, _props$featureFlags2;
          var editor = _ref2.editor,
            view = _ref2.view,
            eventDispatcher = _ref2.eventDispatcher,
            config = _ref2.config,
            dispatchAnalyticsEvent = _ref2.dispatchAnalyticsEvent,
            editorRef = _ref2.editorRef;
          return jsx(BaseTheme, {
            baseFontSize: getBaseFontSize(props.appearance)
          }, jsx(Component, {
            innerRef: editorRef,
            appearance: props.appearance,
            disabled: props.disabled,
            editorActions: editorActions,
            editorDOMElement: editor,
            editorView: view,
            providerFactory: providerFactory,
            eventDispatcher: eventDispatcher,
            dispatchAnalyticsEvent: dispatchAnalyticsEvent,
            maxHeight: props.maxHeight,
            minHeight: props.minHeight,
            onSave: props.onSave ? handleSave : undefined,
            onCancel: props.onCancel,
            popupsMountPoint: props.popupsMountPoint,
            popupsBoundariesElement: props.popupsBoundariesElement,
            popupsScrollableElement: props.popupsScrollableElement,
            contentComponents: config.contentComponents,
            primaryToolbarComponents: config.primaryToolbarComponents,
            primaryToolbarIconBefore: props.primaryToolbarIconBefore,
            secondaryToolbarComponents: config.secondaryToolbarComponents,
            customContentComponents: props.contentComponents,
            customPrimaryToolbarComponents: props.primaryToolbarComponents,
            customSecondaryToolbarComponents: props.secondaryToolbarComponents,
            contextPanel: props.contextPanel,
            collabEdit: props.collabEdit,
            persistScrollGutter: props.persistScrollGutter,
            enableToolbarMinWidth: ((_props$featureFlags = props.featureFlags) === null || _props$featureFlags === void 0 ? void 0 : _props$featureFlags.toolbarMinWidthOverflow) != null ? !!((_props$featureFlags2 = props.featureFlags) !== null && _props$featureFlags2 !== void 0 && _props$featureFlags2.toolbarMinWidthOverflow) : props.allowUndoRedoButtons,
            useStickyToolbar: props.useStickyToolbar,
            featureFlags: featureFlags,
            pluginHooks: config.pluginHooks
          }));
        }
      }), jsx(PortalRenderer, {
        portalProviderAPI: portalProviderAPI
      }));
    }
  }))))));
}